import { useCallback, useEffect, useRef, useState } from 'react';

import { EventType } from '../features/events/types';
import { fetchEvents } from '../services/endpoints';
import { useSpaceContext } from './use-space-context';

export type EventFilters = {
  searchText: string;
};

export type EventsFilterData = {
  data: EventType[];
  pagination: {
    next_cursor: string | null;
    page: number;
    totalCount: number;
    totalPages: number;
    perPage: number;
  };
  status: 'idle' | 'processing' | 'success' | 'error';
};

export const useEventsFilter = () => {
  const { isSpaceReady, spaceExternalId } = useSpaceContext();
  const prevSpaceRef = useRef(spaceExternalId);

  const [filters, setFilters] = useState<EventFilters>({
    searchText: '',
  });

  const [isEventsLoaded, setIsEventsLoaded] = useState(false);
  const [eventsData, setEventsData] = useState<EventsFilterData>({
    data: [],
    pagination: {
      next_cursor: null,
      page: 1,
      totalCount: 0,
      totalPages: 0,
      perPage: 20,
    },
    status: 'idle',
  });

  const buildEventsQuery = useCallback(
    (searchText?: string, page = 1) => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('perPage', '20');

      if (searchText) {
        params.append('searchText', searchText);
      }

      if (spaceExternalId) {
        params.append('spaceExternalIds[]', spaceExternalId);
      }

      return params.toString();
    },
    [spaceExternalId],
  );

  const fetchEventsPage = useCallback(
    async (searchText?: string, page = 1, isNextPage = false) => {
      if (!isSpaceReady) return;

      try {
        setEventsData(prev => ({ ...prev, status: 'processing' }));

        const query = buildEventsQuery(searchText, page);
        const response = await fetchEvents(query);

        setEventsData(prev => ({
          ...prev,
          data: isNextPage
            ? [...prev.data, ...response.events]
            : response.events,
          pagination: response.pagination,
          status: 'success',
        }));

        if (!isEventsLoaded) {
          setIsEventsLoaded(true);
        }
      } catch (error) {
        setEventsData(prev => ({ ...prev, status: 'error' }));
      }
    },
    [buildEventsQuery, isEventsLoaded, isSpaceReady],
  );

  const updateSearchText = useCallback((searchText: string) => {
    setFilters(prev => ({ ...prev, searchText }));
  }, []);

  const fetchNextPage = useCallback(() => {
    const nextPage = eventsData.pagination.page + 1;
    if (nextPage <= eventsData.pagination.totalPages) {
      fetchEventsPage(filters.searchText, nextPage, true);
    }
  }, [eventsData.pagination, filters.searchText, fetchEventsPage]);

  const fetchInitialEvents = useCallback(() => {
    if (!isEventsLoaded && eventsData.status !== 'processing') {
      fetchEventsPage('', 1, false);
    }
  }, [isEventsLoaded, eventsData.status, fetchEventsPage]);

  const resetEvents = useCallback(() => {
    setEventsData({
      data: [],
      pagination: {
        next_cursor: null,
        page: 1,
        totalCount: 0,
        totalPages: 0,
        perPage: 20,
      },
      status: 'idle',
    });
    setIsEventsLoaded(false);
  }, []);

  // Reset the events dropdown when the selected space changes so the new
  // space's events are fetched fresh rather than showing the previous space's.
  useEffect(() => {
    if (prevSpaceRef.current === spaceExternalId) return;
    prevSpaceRef.current = spaceExternalId;
    resetEvents();
  }, [spaceExternalId, resetEvents]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (filters.searchText !== '') {
        // Search with the provided text
        fetchEventsPage(filters.searchText, 1, false);
      } else if (isEventsLoaded && filters.searchText === '') {
        // When search text is cleared and we've previously loaded events, fetch all events
        fetchEventsPage('', 1, false);
      }
    }, 500); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [filters.searchText, fetchEventsPage, isEventsLoaded]);

  return {
    filters,
    isEventsLoaded,
    eventsData,
    updateSearchText,
    fetchEventsPage,
    fetchNextPage,
    fetchInitialEvents,
    resetEvents,
  };
};
